/**
 * Synthetic examples of CS r5b's routes (deliverables 163 and 164), set in "The Drowned Lanterns":
 * session 2's sources (P0-13's synthetic summary and transcript, by hash and size), its open stub
 * capture and its promoted time capture with the two-hour advance, the tentative reveals S1 and
 * S3, the bootstrap steps on the demo, and Undo of the promote's batch. `demo@p2b` holds every row
 * an example names (CS r5b deliverable 167).
 */
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { EXAMPLE_KNOWLEDGE_CREATED } from './examples-ledger.js';
import { EXAMPLE_SESSION_CAPTURE } from './examples-sessions.js';
const ids = EXAMPLE_IDS;
const answer = EXAMPLE_ANSWER_IDS;
const session2 = ids.sessionSaltInTheWells.id;
/** P0-13's synthetic files of session 2 (`packages/fixtures/sources/gma/`), by hash and size. */
export const P2B_SOURCE_FILES = {
    summary: {
        name: 'session_2026_09_05_salt_in_the_wells_part_2.md',
        sha256: '55c093171900c54e98e6f43ce217a3a424be6374af322ea7b7ca2b1e6b87d643',
        bytes: 3324,
    },
    transcript: {
        name: 'session_2026_09_05_salt_in_the_wells_part_2_transcript.vtt',
        sha256: 'c359dbb25fca82c58c37c84c97669c5c21e4a58acece65e210b527873295efe1',
        bytes: 10787,
    },
};
/** A three-line transcript, as a browser sends a `.vtt` it does not know (octet-stream). */
export const EXAMPLE_TRANSCRIPT_FILE = {
    name: 'salt-in-the-wells-table-talk.vtt',
    type: 'application/octet-stream',
    base64: 'V0VCVlRUCgowMDowMDowMS4wMDAgLS0+IDAwOjAwOjAzLjAwMAo8diBHYW1lbWFzdGVyPlRoZSB3ZWxscyBydW4gc2FsdCB0b25pZ2h0Lgo=',
};
/** Session 2's summary source. */
export const EXAMPLE_SOURCE_SUMMARY = {
    id: ids.sourceSaltSummary.id,
    rv: 1,
    sessionId: session2,
    kind: 'gma_summary',
    format: 'md',
    status: 'uploaded',
    asset: {
        assetId: '01a096fd-a380-7000-8000-0000000d1503',
        bytes: P2B_SOURCE_FILES.summary.bytes,
        originalName: P2B_SOURCE_FILES.summary.name,
    },
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** Session 2's transcript source. */
export const EXAMPLE_SOURCE_TRANSCRIPT = {
    ...EXAMPLE_SOURCE_SUMMARY,
    id: ids.sourceSaltTranscript.id,
    kind: 'transcript',
    format: 'vtt',
    asset: {
        assetId: '01a096fd-a380-7000-8000-0000000d1504',
        bytes: P2B_SOURCE_FILES.transcript.bytes,
        originalName: P2B_SOURCE_FILES.transcript.name,
    },
};
/** `sessions.sources.upload`'s answer: the table talk, a transcript. */
export const EXAMPLE_SOURCE_UPLOADED = {
    ...EXAMPLE_SOURCE_TRANSCRIPT,
    id: answer.sessionSourceCreated,
    asset: {
        assetId: answer.sourceAssetCreated,
        bytes: 77,
        originalName: EXAMPLE_TRANSCRIPT_FILE.name,
    },
};
/** Session 2's open stub capture: a ferryman nobody had named. */
export const EXAMPLE_CAPTURE_STUB = {
    ...EXAMPLE_SESSION_CAPTURE,
    id: ids.captureSaltStub.id,
    sessionId: session2,
    seq: 1,
    kind: 'stub',
    body: 'The ferryman who saw the bell go under.',
    docIds: [],
};
/** `sessions.captures.promote`'s answer: the stub linked to Old Sef instead of a new page. */
export const EXAMPLE_CAPTURE_PROMOTED = {
    capture: {
        ...EXAMPLE_CAPTURE_STUB,
        docIds: [ids.npcOldSef.id],
        rv: 2,
        status: 'promoted',
        resolution: { kind: 'link', at: EXAMPLE_NOW, documentId: ids.npcOldSef.id },
    },
    resolution: { kind: 'link', at: EXAMPLE_NOW, documentId: ids.npcOldSef.id },
};
/** Session 2's tentative reveals: S3 (`demo@p1b`) and S1 (`demo@p2b`), neither decided. */
export const EXAMPLE_SESSION_REVEALS = [
    {
        event: {
            ...EXAMPLE_KNOWLEDGE_CREATED,
            id: ids.knowledgeS3Tentative.id,
            secretId: answer.secretS3,
            documentId: null,
            tentative: true,
            pcIds: [],
            how: null,
        },
        label: 'The abbot drowned the bell',
        documentId: ids.npcPaleAbbot.id,
        decided: false,
    },
    {
        event: {
            ...EXAMPLE_KNOWLEDGE_CREATED,
            id: ids.knowledgeS1Tentative.id,
            secretId: answer.secretMaelis,
            documentId: null,
            tentative: true,
            pcIds: [],
            how: null,
        },
        label: 'Her ledger',
        documentId: ids.npcMaelisDorn.id,
        decided: false,
    },
];
const counts = (over) => ({
    create: 0,
    untrash: 0,
    skip: 0,
    update: 0,
    record: 0,
    unchanged: 0,
    ...over,
});
/** A preview's hash: SHA-256 hex (2.J's `previewHash`), here a fixed example value. */
const HASH = 'b3a1c0ffee5a1717b3a1c0ffee5a1717b3a1c0ffee5a1717b3a1c0ffee5a1717';
/** Step 1 on the demo: session 1 exists (skipped), session 4 is created. */
export const EXAMPLE_BOOTSTRAP_SESSIONS = {
    sessions: [{ number: 1 }, { number: 4, realDate: '2026-09-19', title: 'The Drowned Steps' }],
};
export const EXAMPLE_BOOTSTRAP_SESSIONS_PREVIEW = {
    previewHash: HASH,
    counts: counts({ create: 1, skip: 1 }),
    rows: [
        { decision: 'skip', index: 0, number: 1, documentId: ids.sessionLampsOutOnTheQuay.id },
        {
            decision: 'create',
            index: 1,
            number: 4,
            title: 'The Drowned Steps',
            realDate: '2026-09-19',
        },
    ],
};
/** Step 2 on the demo: a Deity's Domain read as canon status. */
export const EXAMPLE_BOOTSTRAP_STATUS = {
    typeKey: 'deity',
    fieldKey: 'domain',
    map: [
        { option: 'Death', canonStatus: 'retired', playState: null },
        { option: 'Tempest', canonStatus: 'canon', playState: null },
    ],
};
export const EXAMPLE_BOOTSTRAP_STATUS_PREVIEW = {
    previewHash: HASH,
    counts: counts({ unchanged: 1 }),
    rows: [{ decision: 'unchanged', documentId: ids.deityDrownedQueen.id, canonStatus: 'canon' }],
};
/** Step 3 on the demo: Maelis Dorn and the Old Quay known before tracking began. */
export const EXAMPLE_BOOTSTRAP_LEDGER = {
    targets: [{ documentId: ids.npcMaelisDorn.id }, { documentId: ids.locationOldQuay.id }],
    mark: 'known',
    asOf: 'before_tracking',
};
export const EXAMPLE_BOOTSTRAP_LEDGER_PREVIEW = {
    previewHash: HASH,
    counts: counts({ record: 2 }),
    rows: [
        { decision: 'record', index: 0, documentId: ids.npcMaelisDorn.id },
        { decision: 'record', index: 1, documentId: ids.locationOldQuay.id },
    ],
};
/** Each apply's hash, the preview's: an example cannot know what 2.J's preview answers. */
export const EXAMPLE_PREVIEW_HASH = HASH;
/** The success skip of an apply: its hash is the preview's, which an example cannot know. */
export const NEEDS_PREVIEW_HASH = {
    skip: "an apply names its preview's hash (2.J's), which an example cannot know",
};
export const EXAMPLE_BOOTSTRAP_APPLIED = (over) => ({ batchId: answer.bootstrapBatch, counts: counts(over) });
/** The promote batch of `demo@p2b`: session 2's time capture and its advance, two rows. */
export const EXAMPLE_CHANGE_BATCH = {
    id: ids.batchSaltTimePromote.id,
    kind: 'sessions.captures.promote',
    origin: 'manual',
    sessionId: session2,
    revertsId: null,
    undoneById: null,
    rowCount: 2,
    createdAt: EXAMPLE_NOW,
};
/** `undo.apply`'s answer: the promote undone by a batch of origin `undo`. */
export const EXAMPLE_UNDO_RESULT = {
    batch: { ...EXAMPLE_CHANGE_BATCH, undoneById: answer.undoBatch },
    undoBatch: {
        ...EXAMPLE_CHANGE_BATCH,
        id: answer.undoBatch,
        kind: 'undo.apply',
        origin: 'undo',
        revertsId: ids.batchSaltTimePromote.id,
        rowCount: 2,
    },
};
//# sourceMappingURL=examples-2b-batches.js.map